import React, { useEffect, useState, useCallback, useRef } from 'react';
import adminApi from '../api/client';

// ── Types ────────────────────────────────────────────────────────────
interface User {
  id: string;
  name: string;
  email: string;
  tier: string;
  xp_points: number | null;
  is_verified: boolean;
  email_verified?: boolean | null;
  is_suspended: boolean;
  strike_count: number | null;
  avatar_url?: string | null;
}

interface UsersResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
}

const mutationError = (error: any, fallback: string): string => {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail
    : typeof detail?.message === 'string' ? detail.message : fallback;
};

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

// ── Toast Component ──────────────────────────────────────────────────
const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`
          flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border
          toast-enter cursor-pointer min-w-[280px]
          ${toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
          }
        `}
        onClick={() => onDismiss(toast.id)}
      >
        <span className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</span>
        <span className="text-sm font-medium flex-1">{toast.message}</span>
        <button className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    ))}
  </div>
);

// ── Confirm Dialog ───────────────────────────────────────────────────
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, title, message, confirmLabel, confirmColor, onConfirm, onCancel, loading,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={loading ? undefined : onCancel} />
      <div role="dialog" aria-label={title} aria-modal="true" className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-slide-up">
        <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6 leading-relaxed">{message}</p>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all ${confirmColor} disabled:opacity-50 active:scale-95`}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processing...
              </span>
            ) : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Badge Components ─────────────────────────────────────────────────
const TierBadge: React.FC<{ tier: string }> = ({ tier }) => {
  const colors: Record<string, string> = {
    free: 'bg-gray-100 text-gray-600',
    premium: 'bg-amber-50 text-amber-700 border border-amber-200',
    pro: 'bg-purple-50 text-purple-700 border border-purple-200',
  };
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${colors[tier?.toLowerCase()] || colors.free}`}>
      {tier || 'Free'}
    </span>
  );
};

const StatusBadge: React.FC<{ active: boolean; trueLabel: string; falseLabel: string }> = ({
  active, trueLabel, falseLabel,
}) => (
  <span className={`
    inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full
    ${active
      ? 'bg-green-50 text-green-700 border border-green-200'
      : 'bg-gray-100 text-gray-500'
    }
  `}>
    {active && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
    {active ? trueLabel : falseLabel}
  </span>
);

// ── Skeleton Row ─────────────────────────────────────────────────────
const SkeletonRow: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 9 }).map((_, i) => (
      <td key={i} className="px-4 py-4">
        <div className="h-4 bg-gray-200 rounded-lg w-3/4" />
      </td>
    ))}
  </tr>
);

// ── Main Users Component ─────────────────────────────────────────────
const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmLabel: string;
    confirmColor: string;
    action: (() => Promise<void>) | null;
  }>({ open: false, title: '', message: '', confirmLabel: '', confirmColor: '', action: null });
  const [actionLoading, setActionLoading] = useState(false);

  const [xpModal, setXpModal] = useState<{
    open: boolean;
    userId: string;
    userName: string;
    currentXp: number;
  }>({ open: false, userId: '', userName: '', currentXp: 0 });
  const [xpAmount, setXpAmount] = useState<string>('');
  const [xpReason, setXpReason] = useState<string>('');
  const [xpLoading, setXpLoading] = useState(false);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const toastIdRef = useRef(0);
  const fetchSequenceRef = useRef(0);
  const mutationInFlightRef = useRef(false);
  const perPage = 20;

  // ── Toast helpers ────────────────────────────────────────────────
  const addToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Fetch users ──────────────────────────────────────────────────
  const fetchUsers = useCallback(async (currentPage: number, query: string) => {
    const sequence = ++fetchSequenceRef.current;
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page: currentPage,
        limit: perPage,
      };
      if (query.trim()) {
        params.search = query.trim();
      }
      const res = await adminApi.get<UsersResponse>('/admin/users', { params });
      if (sequence !== fetchSequenceRef.current) return;
      if (res.data.users.some(user => typeof user.id !== 'string' || !user.id.trim())) {
        throw new Error('User response is missing a canonical account ID');
      }
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err: any) {
      if (sequence !== fetchSequenceRef.current) return;
      setUsers([]);
      setTotal(0);
      addToast('Failed to load users.', 'error');
    } finally {
      if (sequence === fetchSequenceRef.current) setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchUsers(page, searchQuery);
  }, [page, searchQuery, fetchUsers]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    ++fetchSequenceRef.current;
  }, []);

  // ── Debounced search ─────────────────────────────────────────────
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearchQuery(value.trim());
    }, 400);
  };

  // ── User actions ─────────────────────────────────────────────────
  const runUserAction = async (userId: string, action: 'suspend' | 'unsuspend' | 'verify' | 'strike') => {
    if (mutationInFlightRef.current) return;
    mutationInFlightRef.current = true;
    setActionLoading(true);
    const messages = {
      suspend: ['User suspended successfully.', 'Failed to suspend user.'],
      unsuspend: ['User unsuspended successfully.', 'Failed to unsuspend user.'],
      verify: ['Admin badge granted successfully.', 'Failed to grant admin badge.'],
      strike: ['Strike added successfully.', 'Failed to add strike.'],
    };
    try {
      await adminApi.post(`/admin/users/${encodeURIComponent(userId)}/${action}`);
      addToast(messages[action][0], 'success');
      await fetchUsers(page, searchQuery);
    } catch (err: any) {
      addToast(mutationError(err, messages[action][1]), 'error');
    } finally {
      mutationInFlightRef.current = false;
      setActionLoading(false);
    }
  };

  // ── XP adjustment ───────────────────────────────────────────────
  const handleXpAdjust = async () => {
    if (mutationInFlightRef.current) return;
    const amount = parseInt(xpAmount);
    if (isNaN(amount)) return;
    if (!xpReason.trim()) return;

    mutationInFlightRef.current = true;
    setXpLoading(true);
    try {
      const res = await adminApi.post<{ old_xp: number; new_xp: number }>(
        `/admin/users/${encodeURIComponent(xpModal.userId)}/adjust-xp`,
        { amount, reason: xpReason.trim() }
      );
      addToast(`XP updated: ${res.data.old_xp} → ${res.data.new_xp}`, 'success');
      setXpModal({ open: false, userId: '', userName: '', currentXp: 0 });
      setXpAmount('');
      setXpReason('');
      await fetchUsers(page, searchQuery);
    } catch (err: any) {
      addToast(mutationError(err, 'XP adjustment failed'), 'error');
    } finally {
      mutationInFlightRef.current = false;
      setXpLoading(false);
    }
  };

  // ── Confirm action wrapper ──────────────────────────────────────
  const openConfirm = (
    title: string,
    message: string,
    confirmLabel: string,
    confirmColor: string,
    action: () => Promise<void>
  ) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, confirmColor, action });
  };

  const handleConfirm = async () => {
    if (!confirmDialog.action || mutationInFlightRef.current) return;
    await confirmDialog.action();
    setConfirmDialog({ open: false, title: '', message: '', confirmLabel: '', confirmColor: '', action: null });
  };

  // ── Pagination ───────────────────────────────────────────────────
  const totalPages = Math.ceil(total / perPage);
  const mutationLoading = actionLoading || xpLoading;
  const selectionLocked = mutationLoading || confirmDialog.open || xpModal.open;
  const rowActionsDisabled = selectionLocked || search.trim() !== searchQuery;

  return (
    <div className="space-y-6">
      {/* Search bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            id="user-search"
            type="text"
            placeholder="Search by name or email..."
            value={search}
            disabled={selectionLocked}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 bg-white
              text-sm text-gray-800 placeholder-gray-400
              focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
              shadow-sm transition-all duration-200"
          />
          {search && (
            <button
              disabled={selectionLocked}
              aria-label="Clear search"
              onClick={() => {
                if (debounceRef.current) clearTimeout(debounceRef.current);
                setSearch(''); setPage(1); setSearchQuery('');
              }}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span className="font-semibold text-gray-800">{total.toLocaleString()}</span>
          <span>total users</span>
        </div>
      </div>

      <p className="text-sm text-gray-500">
        Email status includes verified Google sign-in; it does not identify the verification method.
        {' '}Admin badge is separate from email verification.
      </p>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" id="users-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {['Name', 'Email', 'Email status', 'Tier', 'XP', 'Admin badge', 'Suspended', 'Strikes', 'Actions'].map((col) => (
                  <th
                    key={col}
                    className="px-4 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <span className="text-4xl">🔍</span>
                      <p className="text-gray-400 font-medium">No users found</p>
                      {search && (
                        <p className="text-gray-400 text-sm">
                          Try a different search term
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gray-50/50 transition-colors duration-150"
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        {/* 
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <span className="text-primary text-xs font-bold">
                            {(user.name || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        */}
                        {user.avatar_url ? (
                          <img
                            src={user.avatar_url}
                            alt={user.name || 'User'}
                            className="w-8 h-8 rounded-full object-cover shrink-0"
                            onError={(e) => {
                              // fallback to letter circle if image fails to load (broken Cloudinary URL etc)
                              (e.target as HTMLImageElement).style.display = 'none';
                              (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={`w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 ${user.avatar_url ? 'hidden' : ''}`}>
                          <span className="text-primary text-xs font-bold">
                            {(user.name || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-gray-800 truncate max-w-[160px]">
                          {user.name || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-600 truncate max-w-[200px]">
                      {user.email}
                    </td>
                    <td className="px-4 py-3.5">
                      {typeof user.email_verified === 'boolean' ? (
                        <StatusBadge active={user.email_verified} trueLabel="Verified" falseLabel="Pending" />
                      ) : (
                        <span className="text-xs font-semibold text-gray-500" title="Email verification status unavailable">
                          Unknown
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <TierBadge tier={user.tier} />
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm font-semibold text-gray-700">
                        {(user.xp_points ?? 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge active={user.is_verified} trueLabel="Verified" falseLabel="Unverified" />
                    </td>
                    <td className="px-4 py-3.5">
                      {user.is_suspended ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                          Suspended
                        </span>
                      ) : (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`
                        text-sm font-bold
                        ${(user.strike_count ?? 0) > 0 ? 'text-coral' : 'text-gray-400'}
                      `}>
                        {user.strike_count ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5">
                        {/* Suspend / Unsuspend */}
                        {user.is_suspended ? (
                          <button
                            disabled={rowActionsDisabled}
                            onClick={() => openConfirm(
                              'Unsuspend User',
                              `Are you sure you want to unsuspend "${user.name || user.email}"? They will regain full access.`,
                              'Unsuspend',
                              'bg-green-600 hover:bg-green-700',
                              () => runUserAction(user.id, 'unsuspend')
                            )}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold
                              bg-green-50 text-green-700 border border-green-200
                              hover:bg-green-100 transition-colors active:scale-95"
                          >
                            Unsuspend
                          </button>
                        ) : (
                          <button
                            disabled={rowActionsDisabled}
                            onClick={() => openConfirm(
                              'Suspend User',
                              `Are you sure you want to suspend "${user.name || user.email}"? They will lose access to Readora.`,
                              'Suspend',
                              'bg-amber hover:bg-amber/90',
                              () => runUserAction(user.id, 'suspend')
                            )}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold
                              bg-amber/10 text-amber border border-amber/20
                              hover:bg-amber/20 transition-colors active:scale-95"
                          >
                            Suspend
                          </button>
                        )}

                        {/* Verify — only if not verified */}
                        {!user.is_verified && (
                          <button
                            disabled={rowActionsDisabled}
                            title="Grant admin badge; does not verify email"
                            onClick={() => runUserAction(user.id, 'verify')}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold
                              bg-primary/10 text-primary border border-primary/20
                              hover:bg-primary/20 transition-colors active:scale-95"
                          >
                            Verify
                          </button>
                        )}

                        {/* +Strike */}
                        <button
                          disabled={rowActionsDisabled}
                          onClick={() => openConfirm(
                            'Add Strike',
                            `Are you sure you want to add a strike to "${user.name || user.email}"? Current strikes: ${user.strike_count ?? 0}.`,
                            '+Strike',
                            'bg-coral hover:bg-coral/90',
                            () => runUserAction(user.id, 'strike')
                          )}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-semibold
                            bg-coral/10 text-coral border border-coral/20
                            hover:bg-coral/20 transition-colors active:scale-95"
                        >
                          +Strike
                        </button>

                        {/* ⚡ XP */}
                        <button
                          disabled={rowActionsDisabled}
                          onClick={() => setXpModal({
                            open: true,
                            userId: user.id,
                            userName: user.name,
                            currentXp: user.xp_points ?? 0,
                          })}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-semibold
                            bg-amber-50 text-amber-700 border border-amber-200
                            hover:bg-amber-100 transition-colors active:scale-95"
                        >
                          ⚡ XP
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
            <span className="text-sm text-gray-500">
              Showing <span className="font-semibold text-gray-700">{(page - 1) * perPage + 1}</span>
              –<span className="font-semibold text-gray-700">{Math.min(page * perPage, total)}</span>
              {' '}of <span className="font-semibold text-gray-700">{total}</span>
            </span>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading || selectionLocked || search.trim() !== searchQuery}
                className="
                  flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium
                  bg-white border border-gray-200 text-gray-600
                  hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                  transition-colors active:scale-95
                "
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Prev
              </button>

              <div className="flex items-center gap-1 px-3">
                <span className="text-sm font-bold text-primary">{page}</span>
                <span className="text-sm text-gray-400">/</span>
                <span className="text-sm text-gray-500">{totalPages}</span>
              </div>

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || loading || selectionLocked || search.trim() !== searchQuery}
                className="
                  flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium
                  bg-white border border-gray-200 text-gray-600
                  hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                  transition-colors active:scale-95
                "
              >
                Next
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Toasts */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        confirmColor={confirmDialog.confirmColor}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        loading={actionLoading}
      />

      {/* XP Adjustment Modal */}
      {xpModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div role="dialog" aria-label="Adjust XP" aria-modal="true" className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            {/* Header */}
            <h3 className="text-lg font-semibold text-gray-900 mb-1">⚡ Adjust XP</h3>
            <p className="text-sm text-gray-500 mb-4">
              {xpModal.userName} · Current XP:{' '}
              <span className="font-semibold text-purple-600">
                {xpModal.currentXp.toLocaleString()}
              </span>
            </p>

            {/* Amount input */}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount (use negative to deduct)
            </label>
            <input
              type="number"
              value={xpAmount}
              onChange={(e) => setXpAmount(e.target.value)}
              placeholder="e.g. 500 or -200"
              className="w-full border border-gray-200 rounded-xl px-4 py-2
                text-sm mb-1 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />

            {/* Live preview */}
            {xpAmount && !isNaN(parseInt(xpAmount)) && (
              <p className="text-xs text-gray-500 mb-3">
                New XP will be:{' '}
                <span className="font-semibold text-emerald-600">
                  {Math.max(0, xpModal.currentXp + parseInt(xpAmount)).toLocaleString()}
                </span>
              </p>
            )}

            {/* Reason input */}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason (required)
            </label>
            <textarea
              value={xpReason}
              onChange={(e) => setXpReason(e.target.value)}
              placeholder="e.g. Compensation for app bug"
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-4 py-2
                text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
            />

            {/* Action label */}
            {xpAmount && !isNaN(parseInt(xpAmount)) && (
              <p className="text-xs font-medium mb-3">
                {parseInt(xpAmount) >= 0 ? (
                  <span className="text-emerald-600">✅ Adding {parseInt(xpAmount)} XP</span>
                ) : (
                  <span className="text-red-500">⚠️ Deducting {Math.abs(parseInt(xpAmount))} XP</span>
                )}
              </p>
            )}

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                disabled={xpLoading}
                onClick={() => {
                  setXpModal({ open: false, userId: '', userName: '', currentXp: 0 });
                  setXpAmount('');
                  setXpReason('');
                }}
                className="flex-1 py-2 rounded-xl border border-gray-200
                  text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleXpAdjust}
                disabled={xpLoading || !xpAmount || !xpReason.trim() || isNaN(parseInt(xpAmount))}
                className="flex-1 py-2 rounded-xl text-sm font-medium text-white
                  bg-amber-500 hover:bg-amber-600
                  disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {xpLoading ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
